import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { BabyProvider } from "@/contexts/BabyContext";
import { BreastfeedTimerProvider } from "@/contexts/BreastfeedTimerContext";
import { DataProvider } from "@/contexts/DataContext";
import { CelebrationProvider } from "@/contexts/CelebrationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import SignInMask from "@/components/SignInMask";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
import CelebrationModal from "@/components/CelebrationModal";

// Eagerly loaded — needed on first paint
import Login from "./pages/Login";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import AlexaAuth from "./pages/AlexaAuth";
import DesktopDemo from "./pages/DesktopDemo"; // TEMPORARY — delete after review
import DesktopSidebar from "./components/DesktopSidebar";

// If a lazy chunk 404s (stale main bundle after a deploy), reload once to
// pick up the fresh index.html with updated chunk URLs.
function lazyWithReload(factory: () => Promise<{ default: any }>) {
  return lazy(() =>
    factory().catch(() => {
      window.location.reload();
      return new Promise<never>(() => {});
    })
  );
}

// Lazily loaded — only fetched when the user navigates to that route
const AddBaby          = lazyWithReload(() => import("./pages/AddBaby"));
const Settings         = lazyWithReload(() => import("./pages/Settings"));
const AddFeed          = lazyWithReload(() => import("./pages/AddFeed"));
const AddDiaper        = lazyWithReload(() => import("./pages/AddDiaper"));
const AddSleep         = lazyWithReload(() => import("./pages/AddSleep"));
const AddGrowth        = lazyWithReload(() => import("./pages/AddGrowth"));
const History          = lazyWithReload(() => import("./pages/History"));
const Insights         = lazyWithReload(() => import("./pages/Insights"));
const Milestones       = lazyWithReload(() => import("./pages/Milestones"));
const Activities       = lazyWithReload(() => import("./pages/Activities"));
const EditBaby         = lazyWithReload(() => import("./pages/EditBaby"));
const InviteCaregiver  = lazyWithReload(() => import("./pages/InviteCaregiver"));
const ManageCaregivers = lazyWithReload(() => import("./pages/ManageCaregivers"));
const AcceptInvite     = lazyWithReload(() => import("./pages/AcceptInvite"));
const Health           = lazyWithReload(() => import("./pages/Health"));
const SolidsPlanner    = lazyWithReload(() => import("./pages/SolidsPlanner"));
const SolidsGuide      = lazyWithReload(() => import("./pages/SolidsGuide"));

const queryClient = new QueryClient();

// Minimal fallback shown while a lazy chunk loads
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <img
      src="/numaa_logo_nobg.png"
      alt="numaa"
      className="w-16 h-16 animate-breathe"
      style={{ objectFit: 'contain' }}
    />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <BabyProvider>
        <CelebrationProvider>
        <BreastfeedTimerProvider>
        <DataProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <DesktopSidebar />
              <div className="lg:ml-[220px]">
              <CelebrationModal />
              <SignInMask />
              <ChunkErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/alexa-auth" element={<AlexaAuth />} />
                  <Route path="/desktop-demo" element={<DesktopDemo />} />{/* TEMPORARY */}
                  <Route path="/invite/:token" element={<AcceptInvite />} />

                  {/* Protected Routes */}
                  <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                  <Route path="/add-baby" element={<ProtectedRoute><AddBaby /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/add-feed" element={<ProtectedRoute><AddFeed /></ProtectedRoute>} />
                  <Route path="/add-diaper" element={<ProtectedRoute><AddDiaper /></ProtectedRoute>} />
                  <Route path="/add-sleep" element={<ProtectedRoute><AddSleep /></ProtectedRoute>} />
                  <Route path="/add-growth" element={<ProtectedRoute><AddGrowth /></ProtectedRoute>} />
                  <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
                  <Route path="/insights" element={<ProtectedRoute><Insights /></ProtectedRoute>} />
                  <Route path="/milestones" element={<ProtectedRoute><Milestones /></ProtectedRoute>} />
                  <Route path="/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
                  <Route path="/edit-baby/:id" element={<ProtectedRoute><EditBaby /></ProtectedRoute>} />
                  <Route path="/manage-caregivers" element={<ProtectedRoute><ManageCaregivers /></ProtectedRoute>} />
                  <Route path="/invite-caregiver" element={<ProtectedRoute><InviteCaregiver /></ProtectedRoute>} />
                  <Route path="/health" element={<ProtectedRoute><Health /></ProtectedRoute>} />
                  <Route path="/solids" element={<ProtectedRoute><SolidsPlanner /></ProtectedRoute>} />
                  <Route path="/solids-guide" element={<ProtectedRoute><SolidsGuide /></ProtectedRoute>} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              </ChunkErrorBoundary>
              </div>
            </BrowserRouter>
          </TooltipProvider>
        </DataProvider>
        </BreastfeedTimerProvider>
        </CelebrationProvider>
      </BabyProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
